import { ArrowDown, ArrowUp, CalendarPlus, Check, History, Images, Plus, TrendingDown, TrendingUp, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Spinner } from "../../components/Spinner";
import { formatCalendarDate, formatTimestamp } from "../../lib/format";
import { uploadPhoto, useAdminMedia, useAdminTripPricing } from "../../lib/queries";
import { OCCUPANCY_INFO, amountWithCurrency } from "../../lib/reservations";
import { WEEKDAY_OPTIONS, departuresPerSeason, distributionLabel, editorOccupancies, groupChanges, newSeasonFrom, parseDepartureDates, pctLabel, rateAmount, weekdaysBetween, withRate } from "../../lib/trips";
import type { Occupancy, PricingHistoryEntry, TripContent, TripGalleryPhoto, TripSeason } from "../../types";
import { Field, Pill, StatTile } from "./shared";

/**
 * The structured half of the trip editor: the rate card (seasons × occupancy, add-ons, currency),
 * the departure calendar, the photo gallery — and the pricing history the rate card feeds every
 * time a version is published. Everything here edits the same universal JSON the textarea shows;
 * the operator just no longer has to type it.
 */

const CURRENCIES = ["USD", "MXN", "EUR"];
const number = (value: string): number | null => {
  const clean = value.replace(/[^\d.]/g, "");
  if (!clean) return null;
  const parsed = Number(clean);
  return Number.isFinite(parsed) ? parsed : null;
};

// --- Rate card -------------------------------------------------------------------------------------

export function PricingEditor({ content, onChange }: { content: TripContent; onChange(next: TripContent): void }) {
  const pricing = content.pricing;
  const currency = pricing.currency || "USD";
  const occupancies = editorOccupancies(pricing.seasons);
  const perSeason = departuresPerSeason(content);
  const [extraOccupancy, setExtraOccupancy] = useState<Occupancy | "">("");

  const setPricing = (patch: Partial<TripContent["pricing"]>) => onChange({ ...content, pricing: { ...pricing, ...patch } });
  const setSeason = (index: number, season: TripSeason) => setPricing({ seasons: pricing.seasons.map((item, i) => (i === index ? season : item)) });
  const removeSeason = (index: number) => setPricing({ seasons: pricing.seasons.filter((_, i) => i !== index) });
  const addSeason = () => setPricing({ seasons: [...pricing.seasons, newSeasonFrom(pricing.seasons)] });
  const setCurrency = (next: string) => setPricing({
    currency: next,
    seasons: pricing.seasons.map((season) => ({ ...season, rates: season.rates.map((rate) => ({ ...rate, currency: next })) })),
    add_ons: pricing.add_ons.map((addon) => ({ ...addon, currency: addon.currency ? next : addon.currency }))
  });
  const addColumn = (occupancy: Occupancy) => setPricing({ seasons: pricing.seasons.map((season) => (rateAmount(season, occupancy) === null ? withRate(season, occupancy, 0, currency) : season)) });
  const setAddon = (index: number, patch: Partial<TripContent["pricing"]["add_ons"][number]>) => setPricing({ add_ons: pricing.add_ons.map((item, i) => (i === index ? { ...item, ...patch } : item)) });

  return (
    <div className="editor-block pricing-editor">
      <div className="section-heading" style={{ margin: 0 }}>
        <div><span className="page-eyebrow">Tarifas</span><h3>Tarifas por temporada y ocupación</h3></div>
        <span className="request-meta">Por persona · {pricing.seasons.length} {pricing.seasons.length === 1 ? "temporada" : "temporadas"}</span>
      </div>
      <p className="field-hint">Cada temporada tiene su propia distribución de tarifas (DBL · TPL · SGL). Al publicar, el cambio queda registrado en el historial de tarifas del viaje.</p>
      <div className="field-grid three">
        <Field label="Moneda" htmlFor="p-currency">
          <select id="p-currency" className="standalone-input" value={currency} onChange={(event) => setCurrency(event.target.value)}>
            {CURRENCIES.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
        <Field label="Mínimo de pasajeros" htmlFor="p-min">
          <input id="p-min" className="standalone-input" inputMode="numeric" value={pricing.minimum_passengers ?? ""} onChange={(event) => setPricing({ minimum_passengers: number(event.target.value) === null ? null : Math.round(number(event.target.value)!) })} placeholder="2" />
        </Field>
        <Field label="Tarifa por" htmlFor="p-per">
          <select id="p-per" className="standalone-input" value={pricing.price_per || "person"} onChange={(event) => setPricing({ price_per: event.target.value })}>
            <option value="person">Persona</option><option value="room">Habitación</option><option value="trip">Viaje completo</option>
          </select>
        </Field>
      </div>
      <Field label="Alcance del precio" htmlFor="p-scope" hint="Aparece bajo el precio: qué cubre y qué no (p. ej. solo servicios terrestres)."><input id="p-scope" className="standalone-input" value={pricing.price_scope} onChange={(event) => setPricing({ price_scope: event.target.value })} maxLength={300} placeholder="Servicios terrestres exclusivamente · no incluye vuelos internacionales" /></Field>

      <div className="table-wrap flat">
        <table className="admin-table rates-table">
          <thead>
            <tr><th>Vigencia</th><th>Categoría</th>{occupancies.map((occupancy) => <th key={occupancy} title={OCCUPANCY_INFO[occupancy].long}>{OCCUPANCY_INFO[occupancy].short}</th>)}<th>Salidas</th><th /></tr>
          </thead>
          <tbody>
            {pricing.seasons.length === 0 && <tr><td colSpan={occupancies.length + 4} className="table-empty">Sin temporadas. Agrega la primera con sus tarifas.</td></tr>}
            {pricing.seasons.map((season, index) => {
              const distribution: Partial<Record<Occupancy, number | null>> = {};
              const base = rateAmount(season, "double");
              for (const occupancy of occupancies) {
                const amount = rateAmount(season, occupancy);
                distribution[occupancy] = amount === null || base === null || base === 0 ? null : ((amount - base) / base) * 100;
              }
              return (
                <tr key={index}>
                  <td>
                    <div className="date-pair">
                      <input className="standalone-input small" type="date" value={season.valid_from ?? ""} onChange={(event) => setSeason(index, { ...season, valid_from: event.target.value || null })} aria-label="Vigencia desde" />
                      <input className="standalone-input small" type="date" value={season.valid_to ?? ""} onChange={(event) => setSeason(index, { ...season, valid_to: event.target.value || null })} aria-label="Vigencia hasta" />
                    </div>
                    {/* Calendar-priced rows (set by the Word import) price only their listed departures. */}
                    {(season.departure_dates?.length ?? 0) > 0 && <small className="field-hint" translate="no">Solo {season.departure_dates!.length} salidas específicas</small>}
                  </td>
                  <td><input className="standalone-input small" value={season.service_category} onChange={(event) => setSeason(index, { ...season, service_category: event.target.value })} placeholder="Primera" aria-label="Categoría" style={{ width: 110 }} /></td>
                  {occupancies.map((occupancy) => {
                    const amount = rateAmount(season, occupancy);
                    return (
                      <td key={occupancy} className="rate-cell">
                        <input className="standalone-input small mono" inputMode="decimal" value={amount ?? ""} onChange={(event) => setSeason(index, withRate(season, occupancy, number(event.target.value), currency))} aria-label={`${OCCUPANCY_INFO[occupancy].long} · temporada ${index + 1}`} placeholder="—" />
                        {occupancy !== "double" && distribution[occupancy] !== null && distribution[occupancy] !== undefined && <small translate="no">{pctLabel(distribution[occupancy])}</small>}
                      </td>
                    );
                  })}
                  <td translate="no">{perSeason[index] ?? 0}</td>
                  <td><button type="button" className="icon-button ghost" onClick={() => removeSeason(index)} aria-label="Quitar temporada"><Trash2 size={15} aria-hidden="true" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="inline-actions">
        <button type="button" className="secondary-button small" onClick={addSeason}><Plus size={14} aria-hidden="true" /> Agregar temporada</button>
        <label className="inline-select">
          <span>Agregar columna</span>
          <select className="standalone-input small" value={extraOccupancy} onChange={(event) => { const value = event.target.value as Occupancy | ""; if (value) addColumn(value); setExtraOccupancy(""); }} aria-label="Agregar ocupación">
            <option value="">Ocupación…</option>
            {(["quadruple", "child"] as Occupancy[]).filter((occupancy) => !occupancies.includes(occupancy)).map((occupancy) => <option key={occupancy} value={occupancy}>{OCCUPANCY_INFO[occupancy].short} · {OCCUPANCY_INFO[occupancy].long}</option>)}
          </select>
        </label>
      </div>

      <span className="field-label" style={{ marginTop: 8 }}>Suplementos y opcionales</span>
      {pricing.add_ons.length === 0 && <p className="field-hint">Sin suplementos. Media pensión, noche extra, traslados…</p>}
      {pricing.add_ons.map((addon, index) => (
        <div className="addon-row" key={index}>
          <input className="standalone-input small" value={addon.name} onChange={(event) => setAddon(index, { name: event.target.value })} placeholder="Nombre" aria-label="Nombre del suplemento" />
          <input className="standalone-input small mono" inputMode="decimal" value={addon.amount ?? ""} onChange={(event) => setAddon(index, { amount: number(event.target.value), currency: addon.currency || currency })} placeholder="Monto" aria-label="Monto" />
          <select className="standalone-input small" value={addon.unit || "person"} onChange={(event) => setAddon(index, { unit: event.target.value })} aria-label="Unidad"><option value="person">por persona</option><option value="room">por habitación</option><option value="booking">por reserva</option></select>
          <input className="standalone-input small" type="date" value={addon.valid_from ?? ""} onChange={(event) => setAddon(index, { valid_from: event.target.value || null })} aria-label="Vigente desde" />
          <input className="standalone-input small" type="date" value={addon.valid_to ?? ""} onChange={(event) => setAddon(index, { valid_to: event.target.value || null })} aria-label="Vigente hasta" />
          {(addon.departure_dates?.length ?? 0) > 0 && <small className="field-hint" translate="no">Se suma en {addon.departure_dates!.length} salidas</small>}
          <button type="button" className="icon-button ghost" onClick={() => setPricing({ add_ons: pricing.add_ons.filter((_, i) => i !== index) })} aria-label="Quitar suplemento"><Trash2 size={15} aria-hidden="true" /></button>
        </div>
      ))}
      <button type="button" className="link-button" onClick={() => setPricing({ add_ons: [...pricing.add_ons, { name: "", description: "", amount: null, currency, unit: "person", valid_from: null, valid_to: null }] })}><Plus size={14} aria-hidden="true" /> Agregar suplemento</button>
    </div>
  );
}

// --- Departure calendar ------------------------------------------------------------------------------

export function DeparturesEditor({ content, onChange }: { content: TripContent; onChange(next: TripContent): void }) {
  const availability = content.availability;
  const [text, setText] = useState(availability.departure_dates.join("\n"));
  const [invalid, setInvalid] = useState<string[]>([]);
  const [range, setRange] = useState<{ from: string; to: string; weekday: number }>({ from: availability.travel_periods[0]?.start_date ?? "", to: availability.travel_periods[0]?.end_date ?? availability.valid_until ?? "", weekday: 6 });
  const setAvailability = (patch: Partial<TripContent["availability"]>) => onChange({ ...content, availability: { ...availability, ...patch } });

  const apply = (value: string) => {
    setText(value);
    const parsed = parseDepartureDates(value);
    setInvalid(parsed.invalid);
    setAvailability({ departure_dates: parsed.dates });
  };
  const generate = () => {
    const generated = weekdaysBetween(range.from, range.to, range.weekday);
    const merged = [...new Set([...availability.departure_dates, ...generated])].sort();
    setText(merged.join("\n"));
    setInvalid([]);
    const plural = WEEKDAY_OPTIONS.find((option) => option.value === range.weekday)?.plural ?? "";
    setAvailability({ departure_dates: merged, arrival_days: availability.arrival_days.includes(plural) || !plural ? availability.arrival_days : [...availability.arrival_days, plural] });
  };
  const upcoming = availability.departure_dates.filter((date) => date >= new Date().toISOString().slice(0, 10)).length;

  return (
    <div className="editor-block">
      <div className="section-heading" style={{ margin: 0 }}>
        <div><span className="page-eyebrow">Salidas</span><h3>Calendario de salidas</h3></div>
        <span className="request-meta" translate="no">{availability.departure_dates.length} fechas · {upcoming} futuras</span>
      </div>
      <div className="field-grid three">
        <Field label="Días de llegada" htmlFor="d-days" hint="Separados por coma: Sábados, Domingos."><input id="d-days" className="standalone-input" value={availability.arrival_days.join(", ")} onChange={(event) => setAvailability({ arrival_days: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} /></Field>
        <Field label="Vigencia de tarifas hasta" htmlFor="d-valid"><input id="d-valid" className="standalone-input" type="date" value={availability.valid_until ?? ""} onChange={(event) => setAvailability({ valid_until: event.target.value || null })} /></Field>
        <Field label="Fechas sin operación (texto)" htmlFor="d-black"><input id="d-black" className="standalone-input" value={availability.blackout_dates_text} onChange={(event) => setAvailability({ blackout_dates_text: event.target.value })} placeholder="Navidad, fin de año, Semana Santa…" /></Field>
      </div>
      <div className="generate-row">
        <select className="standalone-input small" value={range.weekday} onChange={(event) => setRange({ ...range, weekday: Number(event.target.value) })} aria-label="Día de la semana">
          {WEEKDAY_OPTIONS.map((option) => <option key={option.value} value={option.value}>Todos los {option.plural.toLowerCase()}</option>)}
        </select>
        <input className="standalone-input small" type="date" value={range.from} onChange={(event) => setRange({ ...range, from: event.target.value })} aria-label="Desde" />
        <input className="standalone-input small" type="date" value={range.to} onChange={(event) => setRange({ ...range, to: event.target.value })} aria-label="Hasta" />
        <button type="button" className="secondary-button small" onClick={generate} disabled={!range.from || !range.to || range.from > range.to}><CalendarPlus size={14} aria-hidden="true" /> Generar fechas</button>
      </div>
      <Field label="Fechas de salida (AAAA-MM-DD, una por línea)" htmlFor="d-dates">
        <textarea id="d-dates" className="standalone-input textarea mono" rows={6} value={text} onChange={(event) => apply(event.target.value)} spellCheck={false} />
      </Field>
      {invalid.length > 0 && <div className="form-error" role="alert">No se reconocen como fechas: {invalid.join(", ")}</div>}
    </div>
  );
}

// --- Gallery ------------------------------------------------------------------------------------------

export function GalleryField({ ids, initial, onChange }: { ids: string[]; initial: TripGalleryPhoto[]; onChange(ids: string[]): void }) {
  const media = useAdminMedia();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The ordered selection, resolved to a URL and caption from the media catalogue (or the saved gallery while it loads).
  const photos: TripGalleryPhoto[] = ids.map((id) => {
    const fromMedia = media.data?.find((photo) => photo.id === id);
    const fromTrip = initial.find((photo) => photo.id === id);
    return { id, url: fromMedia?.url ?? fromTrip?.url ?? `/api/v1/media/${id}`, title: fromMedia?.title ?? fromTrip?.title ?? null };
  });
  const move = (index: number, delta: number) => {
    const next = [...ids];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const photo = await uploadPhoto(file, { admin: true, kind: "gallery", title: file.name.replace(/\.[^.]+$/, "") });
      await media.refetch();
      onChange([...ids, photo.id]);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "No pudimos subir la imagen."); }
    finally { setUploading(false); }
  };
  const catalog = (media.data ?? []).filter((photo) => photo.active);
  return (
    <div className="field-block">
      <span className="field-label"><Images size={13} aria-hidden="true" /> Galería de la página ({ids.length}{ids.length >= 12 ? " · máximo" : ""})</span>
      <p className="field-hint">Las fotos aparecen bajo el resumen del viaje con su título como pie. El orden es el que se muestra.</p>
      {photos.length > 0 && (
        <ol className="gallery-editor">
          {photos.map((photo, index) => (
            <li key={`${photo.url}-${index}`} style={{ backgroundImage: `url(${photo.url})` }}>
              <span className="gallery-caption">{photo.title ?? "Sin título"}</span>
              <span className="gallery-tools">
                <button type="button" className="icon-button" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Mover antes"><ArrowUp size={13} aria-hidden="true" /></button>
                <button type="button" className="icon-button" onClick={() => move(index, 1)} disabled={index === photos.length - 1} aria-label="Mover después"><ArrowDown size={13} aria-hidden="true" /></button>
                <button type="button" className="icon-button" onClick={() => onChange(ids.filter((id) => id !== photo.id))} aria-label="Quitar de la galería"><X size={13} aria-hidden="true" /></button>
              </span>
            </li>
          ))}
        </ol>
      )}
      <div className="inline-actions">
        <button type="button" className="secondary-button small" onClick={() => setOpen((value) => !value)}>{open ? "Ocultar catálogo" : "Agregar fotos del catálogo"}</button>
        <label className="link-button" style={{ cursor: "pointer" }}>
          {uploading ? "Subiendo…" : "Subir foto nueva"}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(event) => { void upload(event.target.files?.[0]); event.target.value = ""; }} disabled={uploading || ids.length >= 12} />
        </label>
      </div>
      {open && (
        <div className="photo-grid" role="listbox" aria-label="Fotos del catálogo" aria-multiselectable="true">
          {catalog.map((photo) => {
            const selected = ids.includes(photo.id);
            return (
              <button key={photo.id} type="button" role="option" aria-selected={selected} className={`photo-option${selected ? " selected" : ""}`} style={{ backgroundImage: `url(${photo.url})` }} title={photo.title ?? undefined}
                onClick={() => onChange(selected ? ids.filter((id) => id !== photo.id) : ids.length < 12 ? [...ids, photo.id] : ids)}>
                {selected && <span className="photo-check"><Check size={14} strokeWidth={3} aria-hidden="true" /></span>}
                {photo.title && <span className="photo-title">{photo.title}</span>}
              </button>
            );
          })}
          {catalog.length === 0 && <span className="field-hint">El catálogo de fotos está vacío. Sube la primera.</span>}
        </div>
      )}
      {error && <div className="form-error" role="alert">{error}</div>}
    </div>
  );
}

// --- Pricing history ----------------------------------------------------------------------------------

export function PricingHistoryPanel({ tripId }: { tripId: string }) {
  const pricing = useAdminTripPricing(tripId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showSeries, setShowSeries] = useState(false);
  const data = pricing.data;
  const currency = data?.stats.currency || data?.draft.snapshot.currency || "USD";
  const money = (value: number | null | undefined) => (value === null || value === undefined ? "—" : amountWithCurrency(value, currency));

  const seriesRows = useMemo(() => (data?.stats.series ?? []).filter((series) => series.points.length > 1 || series.points.some((point) => point.amount !== null)), [data]);

  if (pricing.isLoading) return <section className="card"><span className="rail-title">Historial de tarifas</span><div className="page-state"><Spinner label="Cargando historial" /></div></section>;
  if (pricing.isError || !data) return <section className="card"><span className="rail-title">Historial de tarifas</span><p className="field-hint">No pudimos cargar el historial. <button type="button" className="link-button" onClick={() => void pricing.refetch()}>Reintentar</button></p></section>;
  const { stats, history, draft } = data;
  const trend = stats.priceFrom?.changePct ?? null;

  return (
    <section className="card pricing-history">
      <div className="section-heading" style={{ margin: 0 }}>
        <div><span className="page-eyebrow"><History size={12} aria-hidden="true" /> Historial de tarifas</span><h3>Tarifas y distribución por versión</h3></div>
        {stats.currentSince && <span className="request-meta">Tarifa vigente desde {formatCalendarDate(stats.currentSince.slice(0, 10))}</span>}
      </div>
      {history.length === 0 ? (
        <p className="field-hint">El historial empieza con la primera publicación: cada versión publicada con tarifas distintas queda registrada aquí con sus cambios.</p>
      ) : (
        <>
          <div className="stat-grid compact">
            <StatTile label="Desde · vigente" value={money(stats.priceFrom?.current)} hint={stats.priceFrom?.first !== null && stats.priceFrom?.first !== undefined ? `primera publicación ${money(stats.priceFrom.first)}` : undefined} compact />
            <StatTile label="Variación" value={trend === null ? "—" : pctLabel(trend)} hint={stats.priceFrom ? `mín. ${money(stats.priceFrom.min)} · máx. ${money(stats.priceFrom.max)}` : undefined} icon={trend !== null && trend < 0 ? TrendingDown : TrendingUp} tone={trend === null ? "default" : trend < 0 ? "ok" : trend > 0 ? "alert" : "default"} compact />
            <StatTile label="Cambios registrados" value={String(stats.changes)} hint={`${stats.entries} ${stats.entries === 1 ? "versión de tarifas" : "versiones de tarifas"}`} compact />
            <StatTile label="Último cambio" value={stats.lastChangeAt ? formatCalendarDate(stats.lastChangeAt.slice(0, 10)) : "Sin cambios"} hint={stats.firstPublishedAt ? `historial desde ${formatCalendarDate(stats.firstPublishedAt.slice(0, 10))}` : undefined} compact />
          </div>
          {distributionLabel(stats.distribution) && (
            <p className="request-meta" translate="no">Distribución vigente respecto a la tarifa doble: {distributionLabel(stats.distribution)}{stats.distribution.double !== undefined ? "" : ""}</p>
          )}
        </>
      )}

      {draft.pendingChanges.length > 0 && (
        <div className="pending-changes" role="status">
          <strong>El borrador guardado (v{draft.version}) cambia la tarifa publicada:</strong>
          <ul>{draft.pendingChanges.map((change, index) => <li key={index} translate="no">{change.text}</li>)}</ul>
          <small>Se registrará en el historial al publicar.</small>
        </div>
      )}

      {history.length > 0 && (
        <ol className="pricing-timeline">
          {history.map((entry) => <PricingEntry key={entry.id} entry={entry} currency={currency} expanded={expanded === entry.id} onToggle={() => setExpanded(expanded === entry.id ? null : entry.id)} />)}
        </ol>
      )}

      {seriesRows.length > 0 && (
        <>
          <button type="button" className="link-button" onClick={() => setShowSeries((value) => !value)}>{showSeries ? "Ocultar evolución por tarifa" : "Ver evolución por tarifa"}</button>
          {showSeries && (
            <div className="table-wrap flat">
              <table className="admin-table series-table">
                <thead><tr><th>Temporada</th><th>Ocupación</th><th>Evolución</th><th>Actual</th></tr></thead>
                <tbody>
                  {seriesRows.map((series) => {
                    const amounts = series.points.map((point) => point.amount);
                    const last = amounts[amounts.length - 1] ?? null;
                    const first = amounts.find((value) => value !== null) ?? null;
                    const change = first !== null && last !== null && first !== 0 ? ((last - first) / first) * 100 : null;
                    return (
                      <tr key={`${series.season}-${series.occupancy}`}>
                        <td>{series.season}</td>
                        <td>{OCCUPANCY_INFO[series.occupancy].short}</td>
                        <td translate="no" className="series-cell">{series.points.map((point, index) => <span key={index} title={`v${point.version} · ${formatCalendarDate(point.publishedAt.slice(0, 10))}`}>{point.amount === null ? "—" : point.amount.toLocaleString("es-MX")}{index < series.points.length - 1 ? " → " : ""}</span>)}</td>
                        <td translate="no"><strong>{money(last)}</strong>{change !== null && change !== 0 && <small className={change < 0 ? "down" : "up"}> {pctLabel(change)}</small>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function PricingEntry({ entry, currency, expanded, onToggle }: { entry: PricingHistoryEntry; currency: string; expanded: boolean; onToggle(): void }) {
  const from = entry.snapshot.priceFrom;
  const groups = groupChanges(entry.changes);
  return (
    <li className={`pricing-entry${entry.status === "current" ? " current" : ""}`}>
      <button type="button" className="pricing-entry-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="pricing-entry-dot" aria-hidden="true" />
        <span className="pricing-entry-copy">
          <span className="label-row">
            <strong translate="no">v{entry.version}</strong>
            <Pill label={entry.status === "current" ? "Vigente" : "Sustituida"} tone={entry.status === "current" ? "ok" : "muted"} />
            <span className="request-meta">{formatTimestamp(entry.publishedAt)}{entry.author ? ` · ${entry.author}` : ""}</span>
          </span>
          <span className="request-meta">
            {entry.changeCount === 0 ? "Tarifa inicial" : `${entry.changeCount} ${entry.changeCount === 1 ? "cambio" : "cambios"}`}
            {entry.note ? ` · ${entry.note}` : ""}
          </span>
        </span>
        <span className="pricing-entry-price" translate="no">{from ? <><small>desde</small><strong>{amountWithCurrency(from.amount, from.currency || currency)}</strong></> : <small>Sin tarifas</small>}</span>
      </button>
      {expanded && (
        <div className="pricing-entry-body">
          {groups.length === 0 && <p className="field-hint">Primera tarifa registrada; no hay versión anterior con la que compararla.</p>}
          {groups.map((group) => (
            <div key={group.title}>
              <span className="field-label">{group.title}</span>
              <ul className="change-list">{group.items.map((change, index) => <li key={index} translate="no">{change.text}</li>)}</ul>
            </div>
          ))}
          <span className="field-label">Tarifas de esta versión</span>
          <div className="table-wrap flat">
            <table className="admin-table" style={{ minWidth: 0 }}>
              <thead><tr><th>Temporada</th>{(["double", "triple", "single", "quadruple", "child"] as Occupancy[]).filter((occupancy) => entry.snapshot.seasons.some((season) => season.rates[occupancy] !== undefined)).map((occupancy) => <th key={occupancy}>{OCCUPANCY_INFO[occupancy].short}</th>)}<th>Distribución</th></tr></thead>
              <tbody>
                {entry.snapshot.seasons.map((season) => (
                  <tr key={season.key}>
                    <td>{season.label}</td>
                    {(["double", "triple", "single", "quadruple", "child"] as Occupancy[]).filter((occupancy) => entry.snapshot.seasons.some((item) => item.rates[occupancy] !== undefined)).map((occupancy) => <td key={occupancy} translate="no">{season.rates[occupancy] === null || season.rates[occupancy] === undefined ? "—" : amountWithCurrency(season.rates[occupancy]!, entry.snapshot.currency || currency)}</td>)}
                    <td translate="no">{distributionLabel(season.distribution) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {entry.snapshot.addOns.length > 0 && <p className="request-meta" translate="no">Suplementos: {entry.snapshot.addOns.map((addon) => `${addon.name} ${addon.amount === null ? "—" : amountWithCurrency(addon.amount, addon.currency || currency)}`).join(" · ")}</p>}
        </div>
      )}
    </li>
  );
}
